// Subscribes to regional_brand_enrichment status for a single region
// key. Returns the row, including progress metadata (processed +
// total + last_invoked_at). Realtime via postgres_changes so the
// banner can render live progress as the edge function ticks
// through chunks — no polling, no extra egress past the initial
// row fetch + the realtime channel.
//
// Usage:
//   const status = useRegionalEnrichmentStatus("us-mo-springfield");
//   if (status?.status === "in_progress") show banner
//   if (status?.status === "done") hide
//
// `regionKey` may be null/undefined — the hook no-ops in that case
// so callers don't need to gate on auth state before mounting.

import { useEffect, useState } from "react";
import { supabase } from "./supabase";

export function useRegionalEnrichmentStatus(regionKey) {
  const [row, setRow] = useState(null);

  useEffect(() => {
    if (!regionKey) {
      setRow(null);
      return;
    }
    let mounted = true;

    // Initial fetch — gets the current state immediately so the
    // banner can show progress on first paint without waiting for
    // a realtime tick.
    supabase
      .from("regional_brand_enrichment")
      .select("*")
      .eq("region_key", regionKey)
      .maybeSingle()
      .then(({ data, error }) => {
        if (!mounted) return;
        if (error) console.warn("[regional-enrich] initial fetch failed:", error.message);
        else console.info("[regional-enrich] initial row for", regionKey, ":", data);
        setRow(data || null);
      });

    // Polling DROPPED 2026-05-03 — realtime + initial fetch is the
    // sole signal source. The trade-off: no live mid-run progress %
    // (the banner just shows a "running in background" hourglass),
    // and we trust the realtime channel to deliver the final
    // status='done' transition. Worst case if realtime drops mid-
    // run: the user sees the hourglass until they re-mount the
    // banner (Settings re-open triggers the initial fetch). That's
    // an acceptable degradation for cutting ~21K queries/day on the
    // Nano-tier disk budget.

    // Realtime subscription — fires on INSERT/UPDATE for this
    // region_key. Each chunk-completion in the edge function
    // upserts the row with new metadata.processed_canonical_ids,
    // so the banner's progress bar tracks live without polling.
    const channel = supabase
      .channel(`region-enrich:${regionKey}`)
      .on("postgres_changes", {
        event: "*",
        schema: "public",
        table: "regional_brand_enrichment",
        filter: `region_key=eq.${regionKey}`,
      }, (payload) => {
        if (!mounted) return;
        if (payload.new) setRow(payload.new);
      })
      .subscribe();

    return () => {
      mounted = false;
      supabase.removeChannel(channel);
    };
  }, [regionKey]);

  return row;
}

// Pull progress numbers out of a row in the shape the banner needs.
// Defensive — older rows may not have metadata.total_canonicals
// populated; falls back to canonicals_count when missing.
export function progressFromRow(row) {
  if (!row) return null;
  const total = Number(row.metadata?.total_canonicals)
    || Number(row.canonicals_count)
    || 0;
  const processed = Array.isArray(row.metadata?.processed_canonical_ids)
    ? row.metadata.processed_canonical_ids.length
    : Number(row.canonicals_count) || 0;
  const pct = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0;
  return {
    status:       row.status,
    displayName:  row.display_name || row.region_key,
    regionKey:    row.region_key,
    processed,
    total,
    pct,
    lastInvoked:  row.metadata?.last_invoked_at || row.updated_at || null,
  };
}
